<template>
  <div class="w-full py-2 flex flex-col justify-center items-center">
    <div v-for="(step, index) in steps" :key="step.id" class="w-full">
      <div class="rounded-xl p-4 bg-gray-200 flex flex-col justify-center w-full">
        <div class="flex items-center justify-between mb-1">
          <span class="font-medium">שלב {{ index + 1 }}</span>
          <Button
            v-if="steps.length > 1"
            aria-label="הסרת שלב"
            class="!w-7 !h-7 !p-0 text-gray-500"
            icon="pi pi-trash"
            severity="secondary"
            text
            @click="removeStep(index)"
          />
        </div>
        <div class="flex flex-col">
          <span class="font-medium mb-1">משך השלב</span>
          <div class="w-full relative">
            <input-number
              v-model="step.duration"
              :maxFractionDigits="2"
              :min="0"
              locale="he-IL"
              style="padding:0;"
            />
            <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">דקות</span>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm mb-2 text-gray-700">תיאור</label>
          <Textarea
            v-model="step.description"
            :rows="1"
            autoResize
            class="w-full border border-gray-200 rounded-lg p-3 text-base resize-y"
          />
        </div>
        <settings-toggle
          class="mt-3"
          :status="!step.requiresProvider"
          title="האם שלב זה המתנה?"
          @value-changed="(val) => {step.requiresProvider = !val}"/>
      </div>

      <div v-if="index < steps.length" class="h-8 relative">
        <div class="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -translate-x-1/2"></div>
      </div>
    </div>
    <Button
      class=" w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-2xl active:scale-95 active:bg-blue-600"
      label="+"
      @click="addStep"
    />
    <div
      v-if="!hasActiveStep"
      class="mt-3 w-full rounded-lg bg-amber-50 border border-amber-300 p-3 text-sm text-amber-800"
    >
      <i class="pi pi-exclamation-triangle ml-1"></i>
      כל השלבים מסומנים כהמתנה, כך שהשירות לא תופס אתכם ביומן — לקוחות יוכלו לקבוע תור נוסף
      בדיוק על גביו. יש לסמן לפחות שלב אחד כשלב עבודה.
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType, ref, watch} from 'vue'
import {IServiceStep} from "@torim-app/types";
import {generateRandomId} from "@app/utils/strings";

const emits = defineEmits(['stepsChanged'])
const props = defineProps({
  preDefinedSteps: {
    required: false,
    type: Array as PropType<Array<IServiceStep>>,
    default: undefined
  }
})

const steps = ref<Array<IServiceStep & {
  id: number | string
  // An empty array counts as "no steps yet" — a service saved as flat keeps `steps: []`, and
  // rendering zero rows left the owner with nothing but a "+" button.
}>>(props.preDefinedSteps?.length ? props.preDefinedSteps.map(x => ({
  ...x,
  id: generateRandomId(5)
})) : [{
  id: 1,
  duration: 0,
  description: '',
  requiresProvider: true
}])

// At least one step has to occupy the provider. An all-waiting service reserves nobody, so
// the scheduler treats its slot as free and lets another booking land on top of it.
const hasActiveStep = computed(() => steps.value.some(step => step.requiresProvider))

const addStep = () => {
  steps.value.push({
    id: Date.now(),
    duration: 0,
    description: '',
    requiresProvider: true
  })
}

const removeStep = (index: number) => {
  steps.value.splice(index, 1)
}

// `immediate` so the parent starts out holding the same steps the form shows. Without it the
// parent kept its own seed until the first keystroke, and the two could disagree on save.
watch(() => steps.value, () => {
  emits('stepsChanged', steps.value)
}, {deep: true, immediate: true})

</script>
